import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Heading,
  SimpleGrid,
  Select,
  Spinner,
  Center,
  Text,
  useColorModeValue,
  HStack,
  Badge,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
} from "@chakra-ui/react";
import { Users, TrendingUp, Award } from "lucide-react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';

// Mock function - ganti dengan import yang sesuai
import { decryptData, URL_WEB_BACKEND } from "CustomUtil";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Warna untuk grafik
const CHART_COLORS = [
  'rgba(72, 187, 120, 0.8)',   // green.500
  'rgba(56, 161, 105, 0.8)',   // green.600
  'rgba(47, 133, 90, 0.8)',    // green.700
  'rgba(39, 103, 73, 0.8)',    // green.800
  'rgba(34, 84, 61, 0.8)',     // green.900
  'rgba(104, 211, 145, 0.8)',  // green.400
  'rgba(154, 230, 180, 0.8)',  // green.300
  'rgba(198, 246, 213, 0.8)',  // green.200
];

const CHART_BORDER_COLORS = [
  'rgba(72, 187, 120, 1)',
  'rgba(56, 161, 105, 1)',
  'rgba(47, 133, 90, 1)',
  'rgba(39, 103, 73, 1)',
  'rgba(34, 84, 61, 1)',
  'rgba(104, 211, 145, 1)',
  'rgba(154, 230, 180, 1)',
  'rgba(198, 246, 213, 1)',
];

export default function GrafikData() {
  const authdata = decryptData(localStorage.getItem("authinfosemakinpas"));
  const [statistics, setStatistics] = useState(null);
  const [summary, setSummary] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("jenis_kelamin");
  const [loading, setLoading] = useState(true);

  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("gray.600", "gray.400");
  const bgBox = useColorModeValue("white", "gray.800");
  const bgCard = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (selectedCategory && selectedCategory !== "all") {
      fetchCategoryData(selectedCategory);
    }
  }, [selectedCategory]);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      // Fetch summary data
      const summaryRes = await fetch(
        `${URL_WEB_BACKEND}/kepegawaians/summary`,
        {
          headers: { Authorization: "Bearer " + authdata },
        }
      );
      const summaryData = await summaryRes.json();
      setSummary(summaryData);

      // Fetch all statistics
      const statsRes = await fetch(
        `${URL_WEB_BACKEND}/kepegawaians/statistics?category=all`,
        {
          headers: { Authorization: "Bearer " + authdata },
        }
      );
      const statsData = await statsRes.json();
      setStatistics(statsData);

    } catch (err) {
      console.error("Gagal memuat data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryData = async (category) => {
    try {
      const res = await fetch(
        `${URL_WEB_BACKEND}/kepegawaians/statistics?category=${category}`,
        {
          headers: { Authorization: "Bearer " + authdata },
        }
      );
      const data = await res.json();

      setStatistics(prev => ({
        ...prev,
        [category]: {
          title: data.title,
          data: data.data
        }
      }));
    } catch (err) {
      console.error(`Gagal memuat data ${category}:`, err);
    }
  };

  const categoryOptions = [
    { value: "jenis_kelamin", label: "Jenis Kelamin" },
    { value: "pangkat_golongan", label: "Pangkat/Golongan" },
    { value: "pendidikan_terakhir", label: "Pendidikan Terakhir" },
    { value: "eselon", label: "Eselon" },
    { value: "jabatan", label: "Jabatan" },
    { value: "hukdis", label: "Hukuman Disiplin" },
  ];

  // Function to prepare chart data
  const prepareBarChartData = (data) => {
    return {
      labels: data.map(item => item.label),
      datasets: [
        {
          label: 'Jumlah Pegawai',
          data: data.map(item => parseInt(item.total)),
          backgroundColor: CHART_COLORS[0],
          borderColor: CHART_BORDER_COLORS[0],
          borderWidth: 1,
        },
      ],
    };
  };

  const preparePieChartData = (data) => {
    return {
      labels: data.map(item => item.label),
      datasets: [
        {
          label: 'Jumlah Pegawai',
          data: data.map(item => parseInt(item.total)),
          backgroundColor: CHART_COLORS,
          borderColor: CHART_BORDER_COLORS,
          borderWidth: 1,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return context.label + ': ' + context.parsed.y + ' pegawai';
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return context.label + ': ' + context.parsed + ' pegawai';
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="green.500" thickness="4px" />
      </Center>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      {summary && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
          <Box bg={bgBox} shadow="md" p={5} rounded="lg">
            <Stat>
              <HStack justify="space-between">
                <Box>
                  <StatLabel color={textColorSecondary}>Total Pegawai</StatLabel>
                  <StatNumber color="green.600" fontSize="3xl">
                    {summary.total_pegawai}
                  </StatNumber>
                  <StatHelpText>Seluruh Pegawai</StatHelpText>
                </Box>
                <Box
                  bg="green.100"
                  p={3}
                  borderRadius="lg"
                >
                  <Icon as={Users} boxSize={8} color="green.600" />
                </Box>
              </HStack>
            </Stat>
          </Box>

          <Box bg={bgBox} shadow="md" p={5} rounded="lg">
            <Stat>
              <HStack justify="space-between">
                <Box>
                  <StatLabel color={textColorSecondary}>Laki-laki</StatLabel>
                  <StatNumber color="blue.600" fontSize="3xl">
                    {summary.total_laki_laki}
                  </StatNumber>
                  <StatHelpText>Pegawai Pria</StatHelpText>
                </Box>
                <Box
                  bg="blue.100"
                  p={3}
                  borderRadius="lg"
                >
                  <Icon as={TrendingUp} boxSize={8} color="blue.600" />
                </Box>
              </HStack>
            </Stat>
          </Box>

          <Box bg={bgBox} shadow="md" p={5} rounded="lg">
            <Stat>
              <HStack justify="space-between">
                <Box>
                  <StatLabel color={textColorSecondary}>Perempuan</StatLabel>
                  <StatNumber color="pink.600" fontSize="3xl">
                    {summary.total_perempuan}
                  </StatNumber>
                  <StatHelpText>Pegawai Wanita</StatHelpText>
                </Box>
                <Box
                  bg="pink.100"
                  p={3}
                  borderRadius="lg"
                >
                  <Icon as={Users} boxSize={8} color="pink.600" />
                </Box>
              </HStack>
            </Stat>
          </Box>

          <Box bg={bgBox} shadow="md" p={5} rounded="lg">
            <Stat>
              <HStack justify="space-between">
                <Box>
                  <StatLabel color={textColorSecondary}>Eselon</StatLabel>
                  <StatNumber color="purple.600" fontSize="3xl">
                    {summary.total_eselon}
                  </StatNumber>
                  <StatHelpText>Jabatan Eselon</StatHelpText>
                </Box>
                <Box
                  bg="purple.100"
                  p={3}
                  borderRadius="lg"
                >
                  <Icon as={Award} boxSize={8} color="purple.600" />
                </Box>
              </HStack>
            </Stat>
          </Box>
        </SimpleGrid>
      )}

      {/* Grafik dengan Tabs */}
      <Box bg={bgBox} p={6} rounded="xl" shadow="md">
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between" align="center">
            <Heading fontSize="xl" color={textColorPrimary}>
              Statistik Data Kepegawaian
            </Heading>
            <Badge colorScheme="green" fontSize="md" px={3} py={1}>
              Visualisasi Data
            </Badge>
          </HStack>

          <Tabs colorScheme="green" variant="enclosed">
            <TabList>
              <Tab>Semua Kategori</Tab>
              <Tab>Filter Kategori</Tab>
            </TabList>

            <TabPanels>
              {/* Tab 1: Semua Kategori */}
              <TabPanel>
                {statistics && (
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                    {/* Jenis Kelamin */}
                    {statistics.jenis_kelamin && (
                      <Box bg={bgCard} p={4} rounded="lg" borderWidth={1} borderColor={borderColor}>
                        <Heading size="sm" mb={4} color={textColorPrimary}>
                          {statistics.jenis_kelamin.title}
                        </Heading>
                        <Box h="250px">
                          <Pie
                            data={preparePieChartData(statistics.jenis_kelamin.data)}
                            options={pieChartOptions}
                          />
                        </Box>
                      </Box>
                    )}

                    {/* Pendidikan */}
                    {statistics.pendidikan_terakhir && (
                      <Box bg={bgCard} p={4} rounded="lg" borderWidth={1} borderColor={borderColor}>
                        <Heading size="sm" mb={4} color={textColorPrimary}>
                          {statistics.pendidikan_terakhir.title}
                        </Heading>
                        <Box h="250px">
                          <Bar
                            data={prepareBarChartData(statistics.pendidikan_terakhir.data)}
                            options={chartOptions}
                          />
                        </Box>
                      </Box>
                    )}

                    {/* Pangkat Golongan */}
                    {statistics.pangkat_golongan && (
                      <Box bg={bgCard} p={4} rounded="lg" borderWidth={1} borderColor={borderColor}>
                        <Heading size="sm" mb={4} color={textColorPrimary}>
                          {statistics.pangkat_golongan.title}
                        </Heading>
                        <Box h="250px">
                          <Bar
                            data={prepareBarChartData(statistics.pangkat_golongan.data)}
                            options={chartOptions}
                          />
                        </Box>
                      </Box>
                    )}

                    {/* Jabatan */}
                    {statistics.jabatan && (
                      <Box bg={bgCard} p={4} rounded="lg" borderWidth={1} borderColor={borderColor}>
                        <Heading size="sm" mb={4} color={textColorPrimary}>
                          {statistics.jabatan.title}
                        </Heading>
                        <Box h="250px">
                          <Bar
                            data={prepareBarChartData(statistics.jabatan.data)}
                            options={chartOptions}
                          />
                        </Box>
                      </Box>
                    )}

                    {/* Eselon */}
                    {statistics.eselon && (
                      <Box bg={bgCard} p={4} rounded="lg" borderWidth={1} borderColor={borderColor}>
                        <Heading size="sm" mb={4} color={textColorPrimary}>
                          {statistics.eselon.title}
                        </Heading>
                        <Box h="250px">
                          <Pie
                            data={preparePieChartData(statistics.eselon.data)}
                            options={pieChartOptions}
                          />
                        </Box>
                      </Box>
                    )}

                    {/* Hukdis */}
                    {statistics.hukdis && (
                      <Box bg={bgCard} p={4} rounded="lg" borderWidth={1} borderColor={borderColor}>
                        <Heading size="sm" mb={4} color={textColorPrimary}>
                          {statistics.hukdis.title}
                        </Heading>
                        <Box h="250px">
                          <Bar
                            data={prepareBarChartData(statistics.hukdis.data)}
                            options={chartOptions}
                          />
                        </Box>
                      </Box>
                    )}
                  </SimpleGrid>
                )}
              </TabPanel>

              {/* Tab 2: Filter Kategori */}
              <TabPanel>
                <VStack align="stretch" spacing={4}>
                  <HStack>
                    <Text fontWeight="semibold" color={textColorPrimary}>
                      Pilih Kategori:
                    </Text>
                    <Select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      maxW="300px"
                      colorScheme="green"
                    >
                      {categoryOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </HStack>

                  {statistics && statistics[selectedCategory] && (
                    <Box bg={bgCard} p={6} rounded="lg" borderWidth={1} borderColor={borderColor}>
                      <Heading size="md" mb={6} color={textColorPrimary}>
                        {statistics[selectedCategory].title}
                      </Heading>

                      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                        {/* Bar Chart */}
                        <Box>
                          <Text fontWeight="semibold" mb={3} color={textColorSecondary}>
                            Grafik Batang
                          </Text>
                          <Box h="300px">
                            <Bar
                              data={prepareBarChartData(statistics[selectedCategory].data)}
                              options={chartOptions}
                            />
                          </Box>
                        </Box>

                        {/* Pie Chart */}
                        <Box>
                          <Text fontWeight="semibold" mb={3} color={textColorSecondary}>
                            Grafik Lingkaran
                          </Text>
                          <Box h="300px">
                            <Pie
                              data={preparePieChartData(statistics[selectedCategory].data)}
                              options={pieChartOptions}
                            />
                          </Box>
                        </Box>
                      </SimpleGrid>

                      {/* Data Table */}
                      <Box mt={6}>
                        <Text fontWeight="semibold" mb={3} color={textColorSecondary}>
                          Detail Data
                        </Text>
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                          {statistics[selectedCategory].data.map((item, index) => (
                            <Box
                              key={index}
                              p={4}
                              bg={bgBox}
                              borderWidth={1}
                              borderColor={borderColor}
                              rounded="md"
                            >
                              <Text fontSize="sm" color={textColorSecondary}>
                                {item.label}
                              </Text>
                              <Text fontSize="2xl" fontWeight="bold" color="green.600">
                                {item.total}
                              </Text>
                              <Text fontSize="xs" color={textColorSecondary}>
                                pegawai
                              </Text>
                            </Box>
                          ))}
                        </SimpleGrid>
                      </Box>
                    </Box>
                  )}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Box>
    </Box>
  );
}